import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  /** Classes do container (padrão: barra de rodapé de tabela). */
  className?: string;
}

const DEFAULT_ROOT_CLASS =
  "border-t border-neutral-200 bg-white px-4 py-3 sm:px-6";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className,
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const summary = (
    <p className="text-center text-sm text-neutral-700 sm:text-left">
      Mostrando <span className="font-medium">{startItem}</span> até{" "}
      <span className="font-medium">{endItem}</span> de{" "}
      <span className="font-medium">{totalItems}</span> resultados
    </p>
  );

  return (
    <div className={className ?? DEFAULT_ROOT_CLASS}>
      <div className="flex w-full flex-col gap-3 sm:hidden">
        {summary}
        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`inline-flex items-center rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium ${
              currentPage === 1
                ? "cursor-not-allowed text-neutral-400"
                : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            Anterior
          </button>
          <span className="text-sm font-medium tabular-nums text-neutral-600">
            Página {currentPage} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`inline-flex items-center rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium ${
              currentPage === totalPages
                ? "cursor-not-allowed text-neutral-400"
                : "text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            Próxima
          </button>
        </div>
      </div>

      <div className="hidden w-full items-center justify-between gap-4 sm:flex">
        {summary}
        <nav
          className="isolate inline-flex shrink-0 -space-x-px rounded-md shadow-sm"
          aria-label="Pagination"
        >
          <button
            type="button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 ${
              currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
            }`}
          >
            <span className="sr-only">Anterior</span>
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>

          {getPageNumbers().map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-neutral-700 ring-1 ring-inset ring-neutral-300"
                >
                  ...
                </span>
              );
            }

            const pageNumber = page as number;
            const isCurrentPage = pageNumber === currentPage;

            return (
              <button
                type="button"
                key={pageNumber}
                onClick={() => onPageChange(pageNumber)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-neutral-300 focus:z-20 focus:outline-offset-0 ${
                  isCurrentPage
                    ? "z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    : "text-neutral-900 hover:bg-neutral-50"
                }`}
              >
                {pageNumber}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-neutral-400 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 focus:z-20 focus:outline-offset-0 ${
              currentPage === totalPages
                ? "cursor-not-allowed opacity-50"
                : ""
            }`}
          >
            <span className="sr-only">Próxima</span>
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
